import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lightning, Check, X, Sparkle } from "@phosphor-icons/react";

export default function AISuggestions({ contactId, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    const run = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/ai/contacts/${contactId}/suggestions`);
        setData(r.data);
      } catch (e) {
        setData({ error: e.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [contactId]);

  if (loading) return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid="ai-suggestions-loading">
      <div className="overline flex items-center gap-2"><Sparkle weight="bold" style={{ color: "var(--accent)" }} /> // suggestions</div>
      <div className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Analyzing…</div>
    </div>
  );
  if (data?.error) return null;
  if (!data?.output) return null;

  const o = data.output;
  const toggle = (key, value) => {
    setSelected((s) => ({ ...s, [key]: s[key] === value ? undefined : value }));
  };
  const toggleTag = (tag) => {
    setSelected((s) => {
      const current = s.tags || [];
      const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
      return { ...s, tags: next };
    });
  };

  const hasSelection =
    selected.stage || selected.priority || selected.relationship_type ||
    (selected.tags && selected.tags.length);

  const apply = async () => {
    if (!hasSelection) return;
    setApplying(true);
    try {
      await api.post(`/ai/apply-suggestion`, {
        contact_id: contactId,
        fields: {
          ...(selected.stage ? { stage: selected.stage } : {}),
          ...(selected.priority ? { priority: selected.priority } : {}),
          ...(selected.relationship_type ? { relationship_type: selected.relationship_type } : {}),
          ...(selected.tags && selected.tags.length ? { tags: selected.tags } : {}),
        },
      });
      toast.success("Suggestions applied");
      setSelected({});
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setApplying(false);
    }
  };

  const Chip = ({ active, confidence, children, onClick, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="tag flex items-center gap-1"
      style={{
        color: active ? "var(--bg)" : "var(--text-2)",
        background: active ? "var(--accent)" : "transparent",
        borderColor: active ? "var(--accent)" : "var(--border)",
        opacity: confidence !== undefined && confidence < 0.3 ? 0.5 : 1,
      }}
    >
      {active ? <Check weight="bold" /> : null}
      {children}
      {confidence !== undefined && (
        <span className="opacity-60 ml-1">{Math.round((confidence || 0) * 100)}%</span>
      )}
    </button>
  );

  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid="ai-suggestions">
      <div className="overline flex items-center gap-2 mb-3">
        <Sparkle weight="bold" style={{ color: "var(--accent)" }} /> // ai suggestions
      </div>
      {o.reasoning && (
        <div className="text-xs mb-3" style={{ color: "var(--text-3)" }} data-testid="suggestions-reasoning">
          {o.reasoning}
        </div>
      )}

      {o.relationship_type?.value && (
        <div className="mb-3">
          <div className="overline mb-1">type</div>
          <Chip
            active={selected.relationship_type === o.relationship_type.value}
            confidence={o.relationship_type.confidence}
            onClick={() => toggle("relationship_type", o.relationship_type.value)}
            testid="suggest-type-chip"
          >
            {o.relationship_type.value}
          </Chip>
        </div>
      )}

      {o.stage?.value && (
        <div className="mb-3">
          <div className="overline mb-1">stage</div>
          <Chip
            active={selected.stage === o.stage.value}
            confidence={o.stage.confidence}
            onClick={() => toggle("stage", o.stage.value)}
            testid="suggest-stage-chip"
          >
            {o.stage.value}
          </Chip>
        </div>
      )}

      {o.priority?.value && (
        <div className="mb-3">
          <div className="overline mb-1">priority</div>
          <Chip
            active={selected.priority === o.priority.value}
            confidence={o.priority.confidence}
            onClick={() => toggle("priority", o.priority.value)}
            testid="suggest-priority-chip"
          >
            {o.priority.value}
          </Chip>
        </div>
      )}

      {(o.tags || []).length > 0 && (
        <div className="mb-3">
          <div className="overline mb-1">tags</div>
          <div className="flex flex-wrap gap-2">
            {o.tags.map((t) => (
              <Chip
                key={t.value}
                active={(selected.tags || []).includes(t.value)}
                confidence={t.confidence}
                onClick={() => toggleTag(t.value)}
                testid={`suggest-tag-${t.value}`}
              >
                #{t.value}
              </Chip>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          onClick={apply}
          disabled={!hasSelection || applying}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="apply-suggestions-btn"
        >
          <Lightning weight="bold" /> Apply selected
        </button>
        {hasSelection && (
          <button
            onClick={() => setSelected({})}
            className="btn-secondary flex items-center gap-2"
            data-testid="clear-suggestions-btn"
          >
            <X weight="bold" /> Clear
          </button>
        )}
      </div>
      <div className="mt-2 text-xs" style={{ color: "var(--text-3)" }}>
        Nothing is applied until you accept.
      </div>
    </div>
  );
}
